export type CountdownFormat = {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
};

const formatUnit = (value: string, unit: string): string => {
  const numValue = parseInt(value, 10);
  if (numValue === 0) return "";
  return `${numValue} ${unit}${numValue === 1 ? "" : "s"}`;
};

export type FormatType =
  | "short"
  | "long"
  | "compact"
  | "mini"
  | "clock"
  | "clock-tight"
  | "match-clock"
  | "compact-smart"
  | "compact-smart-pair"
  | "compact-smart-trio";

export const computeTimeLeft = (nextDate: Date | string | number, nowMs: number = Date.now()): CountdownFormat => {
  const targetDate = nextDate instanceof Date ? nextDate : new Date(nextDate);
  const difference = targetDate.getTime() - nowMs;
  if (difference <= 0) {
    return { days: "00", hours: "00", minutes: "00", seconds: "00" };
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((difference / 1000 / 60) % 60);
  const seconds = Math.floor((difference / 1000) % 60);

  const pad = (n: number) => n.toString().padStart(2, "0");
  return { days: pad(days), hours: pad(hours), minutes: pad(minutes), seconds: pad(seconds) };
};

const isLessThan1minute = (countdown: CountdownFormat) => {
  return countdown.minutes === "00" && countdown.days === "00" && countdown.hours === "00";
};

export const isEmptyCountdown = (countdown: CountdownFormat) => {
  return (
    Number(countdown.days) === 0 &&
    Number(countdown.hours) === 0 &&
    Number(countdown.minutes) === 0 &&
    Number(countdown.seconds) === 0
  );
};

export const formatCountdown = (countdown: CountdownFormat, format: FormatType = "short"): string => {
  switch (format) {
    case "clock": {
      if (countdown.days !== "00") {
        return `${parseInt(countdown.days)}d ${countdown.hours} : ${countdown.minutes} : ${countdown.seconds}`;
      }
      return `${countdown.hours} : ${countdown.minutes} : ${countdown.seconds}`;
    }
    // `MM:SS`, widening to `H:MM:SS` past the hour; zero-padded so the width
    // holds steady while the seconds tick.
    case "match-clock": {
      const pad = (v: string) => parseInt(v, 10).toString().padStart(2, "0");
      const hours = parseInt(countdown.days, 10) * 24 + parseInt(countdown.hours, 10);
      const rest = `${pad(countdown.minutes)}:${pad(countdown.seconds)}`;
      return hours > 0 ? `${hours}:${rest}` : rest;
    }
    case "clock-tight": {
      const pad = (v: string) => parseInt(v, 10).toString().padStart(2, "0");
      const hh = pad(countdown.hours);
      const mm = pad(countdown.minutes);
      const ss = pad(countdown.seconds);
      if (countdown.days !== "00") {
        return `${parseInt(countdown.days)}d ${hh}:${mm}:${ss}`;
      }
      return `${hh}:${mm}:${ss}`;
    }
    case "mini": {
      if (countdown.days !== "00") {
        return `${Number(countdown.days)} day${Number(countdown.days) <= 1 ? "" : "s"} left`;
      }
      if (countdown.hours !== "00") {
        return `${Number(countdown.hours)} hour${Number(countdown.hours) <= 1 ? "" : "s"} left`;
      }
      if (countdown.minutes !== "00") {
        return `${Number(countdown.minutes)} minute${Number(countdown.minutes) <= 1 ? "" : "s"} left`;
      }
      if (isLessThan1minute(countdown)) {
        return "<1m";
      }

      return `${Number(countdown.seconds)} second${Number(countdown.seconds) <= 1 ? "" : "s"} left`;
    }
    case "compact": {
      const parts: string[] = [];
      if (countdown.days !== "00") {
        parts.push(`${parseInt(countdown.days)}d`);
      }
      parts.push(`${parseInt(countdown.hours)}h`);
      parts.push(`${parseInt(countdown.minutes)}m`);
      parts.push(`${parseInt(countdown.seconds)}s`);

      return parts.join(" ");
    }
    case "compact-smart": {
      const days = Number(countdown.days);
      const hours = Number(countdown.hours);
      const minutes = Number(countdown.minutes);
      const seconds = Number(countdown.seconds);

      // Build array of non-zero values with their labels
      const values: Array<{ value: number; label: string }> = [];
      if (days > 0) values.push({ value: days, label: "d" });
      if (hours > 0) values.push({ value: hours, label: "h" });
      if (minutes > 0) values.push({ value: minutes, label: "m" });
      if (seconds > 0) values.push({ value: seconds, label: "s" });

      // Return the two largest non-zero values
      if (values.length === 0) return "0S";
      if (values.length === 1) {
        const first = values[0];
        if (!first) return "0S";
        return `${first.value}${first.label}`;
      }

      // Take first two (largest) values
      const first = values[0];
      const second = values[1];
      if (!first || !second) return "0S";
      return `${first.value}${first.label} ${second.value}${second.label}`;
    }

    case "compact-smart-pair": {
      const days = Number(countdown.days);
      const hours = Number(countdown.hours);
      const minutes = Number(countdown.minutes);
      const seconds = Number(countdown.seconds);

      // Always show two consecutive time units, even if second is zero
      // Priority: days+hours > hours+minutes > minutes+seconds
      if (days > 0) {
        return `${days}d ${hours}h`;
      }
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      }
      if (seconds > 0) {
        return `${seconds}s`;
      }
      return "0s";
    }

    case "compact-smart-trio": {
      const days = Number(countdown.days);
      const hours = Number(countdown.hours);
      const minutes = Number(countdown.minutes);
      const seconds = Number(countdown.seconds);

      // Always show three consecutive time units to keep seconds visible
      if (days > 0) return `${days}d ${hours}h ${minutes}m`;
      if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
      if (minutes > 0) return `${minutes}m ${seconds}s`;
      if (seconds > 0) return `${seconds}s`;
      return "0s";
    }

    case "short":
      return `${countdown.days} d ${countdown.hours} h ${countdown.minutes} min ${countdown.seconds} sec`;

    case "long": {
      const parts = [
        formatUnit(countdown.days, "day"),
        formatUnit(countdown.hours, "hour"),
        formatUnit(countdown.minutes, "minute"),
        formatUnit(countdown.seconds, "second"),
      ];
      return parts.filter(Boolean).join(" ");
    }
  }
};
